/**
 * PersonaFitTab — persona selector, FitScoreDial, vertical
 * WalkthroughTimeline (replacing the legacy accordion), recommendation list,
 * and Phase 2 PDOFitDelta + CreativeProvesSummary slots.
 */

import { Users } from 'lucide-react';
import { clsx } from 'clsx';
import type {
  AdCatalogCreativeReviewSummary,
  LinkedConcept,
  PDOFitConcept,
  WorkspaceBriefAngle,
} from '../types';
import { WalkthroughTimeline } from '../WalkthroughTimeline';
import { PDOFitDelta } from '../PDOFitDelta';
import { CreativeProvesSummary } from '../CreativeProvesSummary';

interface Props {
  reviewSummaries: Record<string, AdCatalogCreativeReviewSummary>;
  latestReview: Record<string, unknown> | null;
  linkedConcepts: LinkedConcept[];
  linkedAngle: WorkspaceBriefAngle | null;
  briefId?: string | null;
  activePersona: string | null;
  onPersonaChange: (id: string) => void;
  onGapClick?: (concept: PDOFitConcept) => void;
  creativeHeadline?: string;
}

import type { AdCatalogCreativeReviewSummary as _ReviewSummary } from '@/services/api';

export function PersonaFitTab({
  reviewSummaries,
  latestReview,
  linkedConcepts,
  linkedAngle,
  activePersona,
  onPersonaChange,
  onGapClick,
  creativeHeadline,
}: Props) {
  const personaIds = Object.keys(reviewSummaries ?? {});
  const focusedReview: _ReviewSummary | null = activePersona
    ? reviewSummaries?.[activePersona] ?? null
    : null;

  const walkthrough = ((latestReview?.cognitive_walkthrough as unknown[]) ??
    []) as Array<Record<string, string>>;
  const recommendations = ((latestReview?.recommendations as unknown[]) ??
    []) as Array<Record<string, unknown>>;

  return (
    <div className="p-5 space-y-6">
      {personaIds.length > 0 && (
        <PersonaSelector
          personaIds={personaIds}
          active={activePersona}
          onSelect={onPersonaChange}
        />
      )}

      {focusedReview ? <FitScoreDial review={focusedReview} /> : <NoReviewPlaceholder />}

      {walkthrough.length > 0 && <WalkthroughTimeline moments={walkthrough} />}

      <PDOFitDelta
        briefAngle={linkedAngle}
        linkedConcepts={linkedConcepts}
        onGapClick={onGapClick}
      />

      {recommendations.length > 0 && <RecommendationList recommendations={recommendations} />}

      <CreativeProvesSummary
        latestReview={latestReview}
        creativeHeadline={creativeHeadline}
        personaId={activePersona}
      />
    </div>
  );
}

function PersonaSelector({
  personaIds,
  active,
  onSelect,
}: {
  personaIds: string[];
  active: string | null;
  onSelect: (id: string) => void;
}) {
  if (personaIds.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-slate-400">
        <Users size={11} />
        Persona
      </div>
      {personaIds.map((pid) => (
        <button
          key={pid}
          onClick={() => onSelect(pid)}
          className={clsx(
            'text-[11px] font-mono px-2.5 py-1 rounded-full border transition-all',
            active === pid
              ? 'bg-teal-500 text-white border-teal-500 shadow-sm'
              : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-600',
          )}
        >
          {pid}
        </button>
      ))}
    </div>
  );
}

const VERDICT_COLOR: Record<string, string> = {
  ready: '#10b981',
  needs_work: '#f59e0b',
  critical: '#ef4444',
};

export function FitScoreDial({ review }: { review: _ReviewSummary }) {
  const score = Number(review.overall_score ?? 0);
  const fraction = Math.max(0, Math.min(1, score / 10));
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = fraction * circumference;
  const verdict = (review.verdict ?? 'unknown') as string;
  const color = VERDICT_COLOR[verdict] ?? '#94a3b8';

  return (
    <div className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="relative flex-shrink-0 w-24 h-24">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="10" />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeDasharray={`${strokeDash} ${circumference}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.8s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-navy-900">{score.toFixed(1)}</span>
          <span className="text-[9px] font-mono text-slate-400">/10</span>
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">verdict</p>
          <p className="text-sm font-bold uppercase tracking-wide" style={{ color }}>
            {verdict.replace(/_/g, ' ')}
          </p>
        </div>
        {review.top_strength && (
          <div>
            <p className="text-[10px] font-mono uppercase text-emerald-500">strength</p>
            <p className="text-xs text-slate-700 leading-snug">{review.top_strength}</p>
          </div>
        )}
        {review.critical_gap && (
          <div>
            <p className="text-[10px] font-mono uppercase text-amber-500">gap</p>
            <p className="text-xs text-slate-700 leading-snug">{review.critical_gap}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function NoReviewPlaceholder() {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
      No creative review run for this persona yet.
    </div>
  );
}

function RecommendationList({
  recommendations,
}: {
  recommendations: Array<Record<string, unknown>>;
}) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Recommendations
      </p>
      {recommendations.map((rec, idx) => (
        <RecommendationCard key={idx} rec={rec} />
      ))}
    </div>
  );
}

function RecommendationCard({ rec }: { rec: Record<string, unknown> }) {
  const priority = String(rec.priority ?? 'medium').toLowerCase();
  const colorClass =
    priority === 'high' || priority === 'critical'
      ? 'bg-rose-50 border-rose-200'
      : priority === 'medium'
        ? 'bg-amber-50 border-amber-200'
        : 'bg-slate-50 border-slate-200';
  const title = (rec.title as string) || (rec.element as string) || 'Recommendation';
  const rationale = (rec.rationale as string) || (rec.persona_rationale as string) || '';
  const expectedImpact = rec.expected_impact as string | undefined;

  return (
    <div className={`rounded-lg border p-3 space-y-1 ${colorClass}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-semibold text-navy-900">{title}</div>
        <span
          className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border flex-shrink-0 ${
            priority === 'high' || priority === 'critical'
              ? 'border-rose-300 text-rose-700 bg-rose-100'
              : priority === 'medium'
                ? 'border-amber-300 text-amber-700 bg-amber-100'
                : 'border-slate-300 text-slate-600 bg-slate-100'
          }`}
        >
          {priority}
        </span>
      </div>
      {rationale && <p className="text-xs text-slate-600 leading-relaxed">{rationale}</p>}
      {expectedImpact && (
        <p className="text-[11px] text-teal-700">
          <span className="font-mono text-[9px] text-teal-500 uppercase mr-1">impact</span>
          {expectedImpact}
        </p>
      )}
      <div className="flex items-center gap-2 pt-1">
        <button
          disabled
          className="text-[11px] text-teal-600 border border-teal-200 disabled:opacity-40 rounded-md px-2 py-0.5 cursor-not-allowed"
        >
          Apply to brief →
        </button>
        <button
          disabled
          className="text-[11px] text-slate-600 border border-slate-200 disabled:opacity-40 rounded-md px-2 py-0.5 cursor-not-allowed"
        >
          Test this variant →
        </button>
      </div>
    </div>
  );
}
